import type { ReactNode } from 'react';
import type { ProblemCopy } from '../api/problem';
import { Button } from './Button';
import { Panel } from './Panel';
import { cx } from './cx';
import { useCopy } from '../strings/useCopy';
import styles from './state.module.css';

/**
 * The three things a page can be instead of its content — waiting, empty, or unable to load — as one
 * shape.
 *
 * Eight screens had written this block out by hand by the end of phase 3, and the copies had drifted in
 * both of the ways a copy drifts. Visually: three different title sizes, three different measures
 * (60ch, 62ch, 72ch) and four different action gaps, so the same failure looked like a different kind
 * of thing depending on which page you were on. And in *behaviour*, which is the one that matters —
 * `/hashtag/TAG` never drew a Try again button, because that page was written from a copy of the search
 * page made before the button existed. {@link ProblemPanel} is why that cannot happen again: it takes
 * the classified {@link ProblemCopy} and reads `retryable` itself, so a screen that has a retry to
 * offer cannot forget to offer it, and a screen that has not cannot invent one.
 *
 * What is deliberately *not* shared is the words. `api/problem.ts` already says why: the taxonomy is
 * common and the sentences are about the subject, so "Video not found" and "this feed does not exist
 * here" stay with their pages.
 *
 * Actions beyond the retry arrive as children rather than as props, which keeps `ui/` free of the
 * router: a `<Link>` is the caller's business and the row it sits in is this component's.
 */
export interface StatePanelProps {
  readonly title: string;
  readonly detail: ReactNode;
  /**
   * The retry, or `null` when offering one would be dishonest. Nullable rather than optional so a call
   * site has to have an opinion — "no button here" is a decision about the failure, not a default.
   */
  readonly onRetry?: (() => void) | null;
  /** Overrides the shared "Try again" — for the few panels whose retry is a different act. */
  readonly retryLabel?: string;
  /** Links, or anything else that belongs beside the button. Rendered in the same row. */
  readonly children?: ReactNode;
  readonly className?: string;
  /** `h1` where the panel *is* the page — a resolve failure, a 404 — and `h2` where it replaces a list. */
  readonly headingLevel?: 'h1' | 'h2';
}

export function StatePanel({
  title,
  detail,
  onRetry,
  retryLabel,
  children,
  className,
  headingLevel = 'h2',
}: StatePanelProps) {
  const Heading = headingLevel;
  // Unconditionally, even though `retryLabel` usually wins: a hook behind a `??` is a hook behind a
  // condition, and React counts them.
  const words = useCopy().text.shell.state;
  const label = retryLabel ?? words.retry;
  /*
   * `!= null` rather than `!== undefined`, and it is load-bearing: every call site passes its extra
   * action as `{href === null ? null : <Link/>}`, so "no action" arrives as `null`. Treating that as
   * present renders an empty flex row, which is invisible except for the panel's own `gap` — a stray
   * band under the paragraph that looks like a spacing bug rather than a missing guard.
   */
  const hasActions = onRetry != null || children != null;

  return (
    <Panel className={cx(styles.panel, className)}>
      <Heading className={styles.title}>{title}</Heading>
      <p className={styles.detail}>{detail}</p>
      {hasActions ? (
        <div className={styles.actions}>
          {onRetry == null ? null : (
            <Button variant="primary" onClick={onRetry}>
              {label}
            </Button>
          )}
          {children}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * A classified failure. The one decision it makes is the one every screen used to make for itself:
 * `retryable` is what puts the button on screen, so the taxonomy in `api/problem.ts` reaches the page
 * intact rather than through eight hand-copies of an `if`.
 *
 * `onRetry` is still required, because a page that cannot refetch is a page whose copy should not be
 * claiming `retryable` in the first place — and passing the callback is cheaper than proving that.
 */
export function ProblemPanel({
  copy,
  onRetry,
  children,
  className,
  headingLevel,
}: {
  readonly copy: ProblemCopy;
  readonly onRetry: () => void;
  readonly children?: ReactNode;
  readonly className?: string;
  readonly headingLevel?: 'h1' | 'h2';
}) {
  return (
    <StatePanel
      title={copy.title}
      detail={copy.detail}
      onRetry={copy.retryable ? onRetry : null}
      className={className}
      headingLevel={headingLevel}
    >
      {children}
    </StatePanel>
  );
}

/**
 * The one sentence a loading page says out loud.
 *
 * It exists because the skeletons are `aria-hidden` — twelve empty boxes read aloud is worse than
 * silence — which means without this line a screen reader is told *nothing at all* while a page loads.
 * `role="status"` rather than `aria-live="assertive"`: it is a progress report, not an interruption.
 */
export function LoadingStatus({ children }: { readonly children: ReactNode }) {
  return (
    <p className={styles.srOnly} role="status">
      {children}
    </p>
  );
}
